import {
  h, ref, computed,
} from "vue";
import AbstractDialogTitle from "~/components/abstract-dialog/title.vue";
import DialogBody from "~/services/buy-credits-dialog/body.vue";
import DialogFooter from "~/services/buy-credits-dialog/footer.vue";
import {
  useDialogStoreV2,
} from "~/components/abstract-dialog/useDialogStoreV2";
import {
  ElMessage,
} from "element-plus";

export function useBuyCreditsDialog(
  onConfirmCallback: (pkg: any) => Promise<any>,
  options: {
    scope?: "personal" | "organization";
    tenant?: string;
    packages?: any[];
  } = {},
) {
  const dialogStoreV2 = useDialogStoreV2();
  const {$t} = useNuxtApp();
  const {
    buildEvidence,
  } = usePaymentCheckout();

  const resolution = defer<[boolean, any | null]>();
  const selectedPackage = ref<any | null>(null);
  const couponCode = ref("");
  const couponNeedsApply = ref(false);
  const loading = ref(false);
  const termsAccepted = ref(false);
  const showTermsError = ref(false);
  const scope = options.scope ?? "personal";

  const disableConfirmButton = computed(() => {
    return !selectedPackage.value || couponNeedsApply.value;
  });

  const {hideDialog} = dialogStoreV2.showDialog({
    titleComponent: h(AbstractDialogTitle, {
      title: scope === "organization"
        ? $t("TENANT_ADMIN.USAGE.ADDON_PURCHASE_DIALOG_TITLE")
        : $t("MY_CREDITS_USAGE.BUY_CREDITS_DIALOG_TITLE"),
    }),
    bodyComponent: () =>
      h(DialogBody, {
        selectedPackage: selectedPackage.value,
        scope,
        tenant: options.tenant ?? "",
        packages: options.packages ?? [],
        onChange: (value: any) => {
          selectedPackage.value = value;
          termsAccepted.value = false;
          showTermsError.value = false;
        },
        onCouponChange: (value: string) => {
          couponCode.value = value;
        },
        onCouponDirtyChange: (value: boolean) => {
          couponNeedsApply.value = value;
        },
      }),
    footerComponent: () =>
      h(DialogFooter, {
        "disableConfirm": disableConfirmButton.value,
        "loading": loading.value,
        "scope": scope,
        "termsAccepted": termsAccepted.value,
        "showTermsError": showTermsError.value,
        "onUpdate:termsAccepted": (value: boolean) => {
          termsAccepted.value = value;
          if (value) {
            showTermsError.value = false;
          }
        },
        "onConfirm": async () => {
          if (!termsAccepted.value) {
            showTermsError.value = true;
            return;
          }
          loading.value = true;
          try {
            const selection = {
              ...selectedPackage.value,
              couponCode: couponCode.value || undefined,
              checkout: buildEvidence($t(scope === "organization"
                ? "TENANT_ADMIN.USAGE.PAY_NOW"
                : "MY_CREDITS_USAGE.PAY_NOW"), "credit_package"),
            };
            const result = await onConfirmCallback(selection);
            resolution.resolve([true, result]);
            hideDialog();
          } catch (err: any) {
            ElMessage.error(
              err?.data?.message
              || err?.message
              || "Failed to initiate payment.",
            );
          } finally {
            loading.value = false;
          }
        },
        "onDecline": () => {
          resolution.resolve([false, null]);
          hideDialog();
        },
      }),
    options: {
      dialogClass: "personal-credit-purchase-dialog !w-[95%] !max-w-6xl rounded-2xl",
      dialogMinWidth: "",
      dialogMinHeight: "",
      // No height cap: the dialog grows with its content and the overlay
      // scrolls the whole thing, so nothing inside it needs to stick or scroll.
      dialogMaxHeight: "",
      alignCenter: false,
      onClose: () => {
        resolution.resolve([false, null]);
      },
      allowCloseByEscape: true,
    },
  });

  return resolution.promise!;
}
